/**
 * 自分自身のプレイヤー（一人称視点の体）。
 *
 * 重力・ジャンプ・当たり判定つきの歩行と、クリエイティブ的な飛行モードを持つ。
 * 当たり判定の解決自体は shared/physics.ts に任せ、ここは速度の作り方
 * （加速・減速・重力・ジャンプ）だけを担当する。
 */

import type { Vec3 } from '../shared/types';
import {
  CollisionWorld,
  bodyIntersects,
  findSafeStandingPosition,
  moveWithCollisions,
} from '../shared/physics';

/** 体の幅（マイクラと同じ 0.6） */
export const PLAYER_WIDTH = 0.6;
/** 体の高さ */
export const PLAYER_HEIGHT = 1.8;
/** 目の高さ（足元から） */
export const EYE_HEIGHT = 1.62;
/** しゃがみ時の目の高さ */
export const SNEAK_EYE_HEIGHT = 1.36;
/** 自動で登れる段差 */
export const STEP_HEIGHT = 0.62;
/** スポーン地点が載る地表の高さ */
export const GROUND_LEVEL = 0;

const BODY = { width: PLAYER_WIDTH, height: PLAYER_HEIGHT };

const GRAVITY = 30;
const JUMP_SPEED = 8.6;
const TERMINAL_VELOCITY = -60;

const WALK_SPEED = 4.3;
const SPRINT_SPEED = 6.3;
const SNEAK_SPEED = 1.4;
const FLY_SPEED = 12;
const FLY_SPRINT_SPEED = 26;

/** 速度が目標に追従する速さ（大きいほどキビキビ動く） */
const GROUND_RESPONSE = 16;
const AIR_RESPONSE = 4;
const FLY_RESPONSE = 12;

/** 1 ステップの最大 dt。タブ復帰などで dt が跳ねても破綻させない。 */
const MAX_STEP = 1 / 60;

/** 落下してもロストしない下限。これを下回ったらスポーン地点へ戻す。 */
const VOID_LIMIT = -64;

export interface MoveIntent {
  /** 前後（+1 が前） */
  forward: number;
  /** 左右（+1 が右） */
  strafe: number;
  /** 飛行時の上下（+1 が上） */
  vertical: number;
  jump: boolean;
  sprint: boolean;
  sneak: boolean;
}

export const IDLE_INTENT: MoveIntent = {
  forward: 0,
  strafe: 0,
  vertical: 0,
  jump: false,
  sprint: false,
  sneak: false,
};

export class LocalPlayer {
  /** 足元（当たり判定ボックスの下面中心）のワールド座標 */
  position: Vec3 = { x: 0.5, y: 0, z: 6.5 };
  velocity: Vec3 = { x: 0, y: 0, z: 0 };
  /** 水平方向の向き（ラジアン、0 で -Z 方向を向く） */
  yaw = 0;
  /** 上下の向き（ラジアン、+ が上） */
  pitch = 0;

  onGround = false;
  flying = false;
  sneaking = false;
  sprinting = false;
  /** 水平方向に動いているか（アバターの歩行アニメ用） */
  moving = false;

  private readonly spawn: Vec3;

  constructor(spawn?: Vec3) {
    if (spawn) this.position = { ...spawn };
    this.spawn = { ...this.position };
  }

  get eyeHeight(): number {
    return this.sneaking && !this.flying ? SNEAK_EYE_HEIGHT : EYE_HEIGHT;
  }

  eyePosition(): Vec3 {
    return {
      x: this.position.x,
      y: this.position.y + this.eyeHeight,
      z: this.position.z,
    };
  }

  /** 視線方向の単位ベクトル */
  lookDirection(): Vec3 {
    const cosPitch = Math.cos(this.pitch);
    return {
      x: -Math.sin(this.yaw) * cosPitch,
      y: Math.sin(this.pitch),
      z: -Math.cos(this.yaw) * cosPitch,
    };
  }

  toggleFly(): void {
    this.flying = !this.flying;
    if (this.flying) {
      this.velocity.y = 0;
      this.onGround = false;
    }
  }

  respawn(world: CollisionWorld): void {
    this.position = findSafeStandingPosition(world, this.spawn, BODY);
    this.velocity = { x: 0, y: 0, z: 0 };
    this.onGround = false;
  }

  /** 保存した姿勢へ復元する（スポーン地点自体は変えない） */
  setPose(position: Vec3, yaw: number, pitch: number, flying = false): void {
    this.position = { ...position };
    this.yaw = yaw;
    this.pitch = pitch;
    this.velocity = { x: 0, y: 0, z: 0 };
    this.onGround = false;
    this.flying = flying;
  }

  /** 足元すぐ下に床／ブロックがあるか */
  isSupported(world: CollisionWorld): boolean {
    const probe = moveWithCollisions(
      world,
      this.position,
      BODY,
      { x: 0, y: -0.1, z: 0 },
      { stepHeight: 0 }
    );
    return probe.onGround;
  }

  /**
   * ブロックに埋まっている場合は真上の空きへ逃がす。
   * それでも足元が空中なら飛行だったとみなす（古い保存でリロード直後に落下するのを防ぐ）。
   */
  ensureClear(world: CollisionWorld): void {
    this.position = findSafeStandingPosition(world, this.position, BODY);
    if (!this.flying && !this.isSupported(world)) {
      this.flying = true;
      this.velocity.y = 0;
    }
  }

  /** dt 秒ぶん状態を進める。dt が大きいときは分割して安定させる。 */
  update(dt: number, intent: MoveIntent, world: CollisionWorld): void {
    let remaining = Math.min(dt, 0.25);
    while (remaining > 0) {
      const step = Math.min(remaining, MAX_STEP);
      this.step(step, intent, world);
      remaining -= step;
    }
  }

  private step(dt: number, intent: MoveIntent, world: CollisionWorld): void {
    this.sneaking = intent.sneak;
    const horizontalInput = Math.hypot(intent.forward, intent.strafe);
    this.sprinting = intent.sprint && intent.forward > 0;

    // --- 目標速度（ワールド座標系） ---------------------------------
    const speed = this.flying
      ? intent.sprint
        ? FLY_SPRINT_SPEED
        : FLY_SPEED
      : this.sneaking
        ? SNEAK_SPEED
        : this.sprinting
          ? SPRINT_SPEED
          : WALK_SPEED;

    let desiredX = 0;
    let desiredZ = 0;
    if (horizontalInput > 1e-6) {
      const scale = speed / Math.max(1, horizontalInput);
      const forwardX = -Math.sin(this.yaw);
      const forwardZ = -Math.cos(this.yaw);
      const rightX = Math.cos(this.yaw);
      const rightZ = -Math.sin(this.yaw);
      desiredX = (forwardX * intent.forward + rightX * intent.strafe) * scale;
      desiredZ = (forwardZ * intent.forward + rightZ * intent.strafe) * scale;
    }

    const response = this.flying ? FLY_RESPONSE : this.onGround ? GROUND_RESPONSE : AIR_RESPONSE;
    const blend = 1 - Math.exp(-response * dt);
    this.velocity.x += (desiredX - this.velocity.x) * blend;
    this.velocity.z += (desiredZ - this.velocity.z) * blend;

    // --- 垂直方向 ---------------------------------------------------
    if (this.flying) {
      const desiredY = intent.vertical * speed;
      this.velocity.y += (desiredY - this.velocity.y) * blend;
    } else {
      if (intent.jump && this.onGround) {
        this.velocity.y = JUMP_SPEED;
        this.onGround = false;
      }
      this.velocity.y = Math.max(this.velocity.y - GRAVITY * dt, TERMINAL_VELOCITY);
    }

    // --- 衝突解決 ---------------------------------------------------
    const wasOnGround = this.onGround;
    const before = this.position;
    const oneWayY = world.oneWayFloorY;
    const onOneWayFloor =
      oneWayY !== null && Math.abs(before.y - oneWayY) <= 1e-4;
    const passThroughOneWay = intent.sneak && (this.flying || onOneWayFloor);
    const result = moveWithCollisions(
      world,
      before,
      BODY,
      {
        x: this.velocity.x * dt,
        y: this.velocity.y * dt,
        z: this.velocity.z * dt,
      },
      {
        stepHeight: this.flying ? 0 : STEP_HEIGHT,
        wasOnGround,
        passThroughOneWay,
      }
    );

    let next = result.position;
    const droppedThroughOneWay =
      passThroughOneWay &&
      oneWayY !== null &&
      before.y >= oneWayY - 1e-4 &&
      next.y < oneWayY - 1e-4;

    // しゃがみ中は足場から落ちない（マイクラのスニーク）。
    // グリッドをすり抜けるときは Y を落とすので、接地ラッチだけ外す。
    if (this.sneaking && !this.flying && wasOnGround && !result.onGround && !droppedThroughOneWay) {
      next = { x: before.x, y: next.y, z: before.z };
      this.velocity.x = 0;
      this.velocity.z = 0;
    }

    this.position = next;
    this.onGround =
      result.onGround ||
      (this.sneaking && wasOnGround && !this.flying && !droppedThroughOneWay);

    if (result.onGround && this.velocity.y < 0) this.velocity.y = 0;
    if (result.hitCeiling && this.velocity.y > 0) this.velocity.y = 0;
    if (result.hitWall && dt > 1e-8) {
      // 実際に進めた水平変位だけを速度に残す。壁に押し込んだ成分が残ると加速する。
      this.velocity.x = (this.position.x - before.x) / dt;
      this.velocity.z = (this.position.z - before.z) / dt;
    }
    if (this.onGround && this.flying && this.velocity.y <= 0) this.flying = false;

    this.moving = Math.hypot(this.position.x - before.x, this.position.z - before.z) > 1e-4;

    if (this.position.y < VOID_LIMIT) this.respawn(world);
  }

  /** 体が何かに埋まっているか（設置位置の判定に使う） */
  isStuck(world: CollisionWorld): boolean {
    return bodyIntersects(world, this.position, BODY);
  }
}

/** プレイヤーの当たり判定サイズ（他モジュールから参照する） */
export const PLAYER_BODY = BODY;
